import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import Avatar from "@/components/Avatar";
import AttachmentComposer from "@/components/AttachmentComposer";
import { Button } from "@/components/ui/button";
import { Send, Video, Phone, Hand, Paperclip } from "lucide-react";
import { toast } from "sonner";
import * as log from "@/lib/logger";
import { isOnline as isEffectivelyOnline, formatLastSeen, formatExact } from "@/lib/presence";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const isImageAttachment = (a) => (a?.mime || "").startsWith("image/");

export default function DmView({ subscribe, onOpenCall, presence }) {
  const { convId } = useParams();
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  // Visual state that plays a short "you were poked" animation whenever a
  // nudge arrives for this conversation.
  const [pokeFlash, setPokeFlash] = useState(false);
  const [poking, setPoking] = useState(false);
  // Tick every 30s so the "Last seen 5m ago" copy stays fresh without a full
  // presence refetch.
  const [, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(iv);
  }, []);
  const listRef = useRef(null);

  useEffect(() => {
    if (!convId) return;
    let cancelled = false;
    (async () => {
      try {
        const cs = await api.get("/conversations");
        if (cancelled) return;
        setConv(cs.data.find((c) => c.id === convId));
      } catch (err) {
        log.debug("[dm] load conv failed", err);
      }
      try {
        const m = await api.get(`/conversations/${convId}/messages`);
        if (cancelled) return;
        setMsgs(m.data);
        setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight }), 50);
      } catch (err) {
        log.debug("[dm] load messages failed", err);
        setMsgs([]);
      }
      // Mark this conversation as read the moment it's opened.
      api.post(`/conversations/${convId}/read`).catch(() => {});
    })();
    return () => { cancelled = true; };
  }, [convId]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((type, payload) => {
      if (type === "dm.new" && payload?.conversation_id === convId) {
        setMsgs((prev) => (prev.find((m) => m.id === payload.message.id) ? prev : [...prev, payload.message]));
        setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }), 20);
      } else if (
        type === "nudge" &&
        payload?.conversation_id === convId &&
        (payload?.to_user_ids || []).includes(user?.id)
      ) {
        toast(`👋 ${payload.from_user_name || "Someone"} poked you`);
        setPokeFlash(true);
        setTimeout(() => setPokeFlash(false), 900);
      }
    });
  }, [subscribe, convId, user?.id]);

  const send = async () => {
    if (!text.trim() || !convId) return;
    const t = text; setText("");
    const { data } = await api.post(`/conversations/${convId}/messages`, { text: t });
    setMsgs((prev) => (prev.find((m) => m.id === data.id) ? prev : [...prev, data]));
    setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }), 20);
  };

  const sendAttachment = async ({ attachment, caption }) => {
    if (!convId) return;
    const { data } = await api.post(`/conversations/${convId}/messages`, {
      text: caption || `📎 ${attachment.filename}`,
      attachments: [attachment],
    });
    setMsgs((prev) => (prev.find((m) => m.id === data.id) ? prev : [...prev, data]));
    setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }), 20);
  };

  const startCall = async (kind) => {
    try {
      const { data } = await api.post("/calls/start", {
        room: `dm-${convId}`,
        kind,
        dm_id: convId,
      });
      onOpenCall(data);
    } catch (e) {
      toast.error("Could not start the call");
    }
  };

  const nudge = async () => {
    if (poking) return;
    setPoking(true);
    try {
      await api.post(`/conversations/${convId}/nudge`);
      toast.success("👋 Poke sent");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Could not send poke");
    } finally {
      setTimeout(() => setPoking(false), 1200);
    }
  };

  if (!convId) return <div className="p-6 text-sm text-zinc-500">Select a conversation from the sidebar.</div>;
  const other = conv?.members?.find((m) => m?.id !== user?.id) || conv?.members?.[0];
  const otherPresence = other?.id ? presence?.[other.id] : null;
  const otherOnline = isEffectivelyOnline(otherPresence);
  const otherStatus = otherOnline ? (otherPresence?.status || "available") : "offline";
  // Prefer live last_seen, then persisted last_seen from user document.
  const otherLastSeenRaw = otherPresence?.last_seen_at || other?.last_seen || null;
  const lastSeenLabel = otherOnline
    ? "Active now"
    : (formatLastSeen(otherLastSeenRaw) ? `Last seen ${formatLastSeen(otherLastSeenRaw)}` : "Offline");

  return (
    <div className={`flex-1 flex flex-col overflow-hidden bg-white relative ${pokeFlash ? "poke-flash" : ""}`} data-testid="dm-view">
      <div className="h-14 border-b flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-3">
          <Avatar user={other} size="md" status={otherStatus} online={otherOnline}/>
          <div>
            <div className="font-semibold text-sm">{other?.name}</div>
            <div
              className="text-xs text-zinc-500 flex items-center gap-1.5"
              data-testid="dm-presence-line"
              title={otherLastSeenRaw ? formatExact(otherLastSeenRaw) : ""}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: otherOnline ? "#10B981" : "#9CA3AF" }}
              />
              {lastSeenLabel}
            </div>
          </div>
        </div>
        <div className="flex gap-1 items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={nudge}
            disabled={poking}
            title="Poke this person"
            data-testid="dm-nudge"
            className={`text-zinc-600 hover:text-[var(--brand-accent)] ${poking ? "opacity-60" : ""}`}
          >
            <Hand className={`w-4 h-4 transition-transform ${poking ? "animate-wave" : ""}`}/>
          </Button>
          <Button variant="ghost" size="icon" title="Start a voice call" data-testid="dm-voice-call" onClick={() => startCall("voice")}>
            <Phone className="w-4 h-4"/>
          </Button>
          <Button variant="ghost" size="icon" title="Start a video call" data-testid="dm-video-call" onClick={() => startCall("video")}>
            <Video className="w-4 h-4"/>
          </Button>
        </div>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto scroll-thin p-6 space-y-3">
        {msgs.map((m) => {
          const own = m.user_id === user?.id;
          const atts = m.attachments || [];
          const first = atts[0];
          const isImageMsg = first && isImageAttachment(first);
          const caption = (m.text && !m.text.startsWith("📎 ")) ? m.text : "";
          return (
            <div key={m.id} className={`flex gap-2 ${own ? "flex-row-reverse" : ""}`}>
              <Avatar user={m.user} size="sm"/>
              <div className={`max-w-md flex flex-col ${own ? "items-end" : "items-start"}`}>
                {/* WhatsApp-style unified image + caption bubble. */}
                {isImageMsg && (
                  <div
                    className={`overflow-hidden rounded-2xl shadow-sm ${
                      own ? "bg-[var(--brand-primary)]" : "bg-zinc-100"
                    }`}
                    data-testid={`dm-att-bubble-${first.id || first.stored_as}`}
                  >
                    <a
                      href={BACKEND + first.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block"
                      data-testid={`dm-att-image-${first.id || first.stored_as}`}
                    >
                      <img
                        src={BACKEND + first.url}
                        alt={first.filename || ""}
                        className="w-full max-w-xs max-h-72 object-cover"
                      />
                    </a>
                    {caption && (
                      <div className={`px-3 py-2 text-sm whitespace-pre-wrap break-words ${own ? "text-white" : "text-zinc-900"}`}>
                        {caption}
                      </div>
                    )}
                  </div>
                )}
                {/* Documents render as chips (unchanged). */}
                {!isImageMsg && atts.map((a) => (
                  <a
                    key={a.id || a.url}
                    href={BACKEND + a.url}
                    target="_blank"
                    rel="noreferrer"
                    className={`mb-1 inline-flex items-center gap-2 text-xs rounded-full px-2.5 py-1 border ${
                      own
                        ? "bg-white/15 text-white border-white/20 hover:bg-white/25"
                        : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                    }`}
                    data-testid={`dm-att-doc-${a.id || a.stored_as}`}
                  >
                    <Paperclip className="w-3 h-3"/> {a.filename}
                  </a>
                ))}
                {/* Standalone text bubble only when there is no image caption. */}
                {(!isImageMsg && m.text && !m.text.startsWith("📎 ")) && (
                  <div className={`rounded-2xl px-3 py-2 text-sm break-words ${own ? "bg-[var(--brand-primary)] text-white" : "bg-zinc-100"}`}>
                    {m.text}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t p-4">
        <div className="flex items-end gap-2 border rounded-lg p-2 focus-within:border-zinc-400">
          <AttachmentComposer onSend={sendAttachment}/>
          <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
            className="flex-1 resize-none outline-none text-sm max-h-40" placeholder="Message…" data-testid="dm-input"/>
          <Button size="sm" className="bg-[var(--brand-accent)] text-white" onClick={send} data-testid="dm-send"><Send className="w-3.5 h-3.5"/></Button>
        </div>
      </div>

      {/* Big playful overlay when the other person pokes us */}
      {pokeFlash && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-40" data-testid="dm-poke-flash">
          <div className="text-6xl select-none animate-poke-pop">👋</div>
        </div>
      )}
    </div>
  );
}
